// Helper to safely parse JSONB fields that might be strings or already parsed
export function parseJsonField(field) {
  if (!field) return null;
  if (typeof field === 'string') {
    try {
      return JSON.parse(field);
    } catch {
      return null;
    }
  }
  return field;
}

// Get array of URLs from products.image_urls JSONB
// Structure: { images: [{ id, url }], totalCount, imagesFound }
export function getProductImageUrls(imageUrlsField) {
  const parsed = parseJsonField(imageUrlsField);
  if (!parsed) return [];
  
  // Handle the nested structure
  if (parsed.images && Array.isArray(parsed.images)) {
    return parsed.images
      .map(img => img?.url || img)
      .filter(url => typeof url === 'string' && url.length > 0);
  }
  
  // If it's already an array of URLs
  if (Array.isArray(parsed)) {
    return parsed.filter(url => typeof url === 'string' && url.length > 0);
  }
  
  return [];
}

// Get images from static_ads ad*_images JSONB
// Structure: { image1, image2, image3, image4 }
export function getStaticAdImages(adImagesField) {
  const parsed = parseJsonField(adImagesField);
  if (!parsed) return [];
  
  const images = [];
  for (let i = 1; i <= 10; i++) {
    const img = parsed[`image${i}`];
    if (img && typeof img === 'string') {
      images.push(img);
    }
  }
  return images;
}

// Generic URL extractor for any JSONB field
export function getUrlsFromJsonb(field) {
  const parsed = parseJsonField(field);
  if (!parsed) return [];
  
  // If it has images array (products.image_urls structure)
  if (parsed.images && Array.isArray(parsed.images)) {
    return parsed.images
      .map(img => img?.url || img)
      .filter(url => typeof url === 'string' && url.length > 0);
  }
  
  // If it's an object with image1, image2, etc keys (static_ads structure)
  if (typeof parsed === 'object' && !Array.isArray(parsed)) {
    const urls = [];
    Object.keys(parsed).forEach(key => {
      if (key.startsWith('image') && typeof parsed[key] === 'string') {
        urls.push(parsed[key]);
      }
    });
    if (urls.length > 0) return urls;
  }
  
  // If it's a plain array
  if (Array.isArray(parsed)) {
    return parsed.filter(url => typeof url === 'string' && url.length > 0);
  }
  
  return [];
}

// Get first valid URL from JSONB
export function getFirstUrlFromJsonb(field) {
  const urls = getUrlsFromJsonb(field);
  return urls.length > 0 ? urls[0] : null;
}

// Calculate stage metrics for products
export function calculateStageMetrics(products, relatedData) {
  const metrics = {
    sniped: { total: 0, completed: 0, inProgress: 0 },
    researching: { total: 0, completed: 0, inProgress: 0 },
    creative: { total: 0, completed: 0, inProgress: 0 },
    winning: { total: 0, completed: 0, inProgress: 0 },
    shopify: { total: 0, completed: 0, inProgress: 0 }
  };

  products.forEach(product => {
    const data = relatedData[product.ecom_id] || {};
    
    // Sniped = product exists with basic data
    metrics.sniped.total++;
    if (product.product_status === 'Product Details Completed') {
      metrics.sniped.completed++;
    } else if (product.product_status === 'Product Details in Progress') {
      metrics.sniped.inProgress++;
    }

    // Researching = has reviews and marketing angles
    const hasReviews = !!data.reviews;
    const hasMarketing = !!data.marketing;
    if (hasReviews || hasMarketing) {
      metrics.researching.total++;
      const reviewsCompleted = data.reviews?.review_status === 'Reviews Completed';
      const marketingCompleted = data.marketing?.status === 'Completed';
      if (reviewsCompleted && marketingCompleted) {
        metrics.researching.completed++;
      } else if (reviewsCompleted || marketingCompleted || 
                 data.reviews?.review_status === 'Reviews In Progress' || 
                 data.marketing?.status === 'In Progress') {
        metrics.researching.inProgress++;
      }
    }

    // Creative = has UGC ads or static ads
    const hasUgc = !!data.ugc;
    const hasStaticAds = !!data.staticAds;
    if (hasUgc || hasStaticAds) {
      metrics.creative.total++;
      const ugcCompleted = data.ugc?.status === 'Completed';
      const staticCompleted = data.staticAds?.status === 'Generation Complete';
      if (ugcCompleted && staticCompleted) {
        metrics.creative.completed++;
      } else if (ugcCompleted || staticCompleted ||
                 data.ugc?.status === 'In Progress' ||
                 data.staticAds?.status?.includes('In Progress')) {
        metrics.creative.inProgress++;
      }
    }

    // Winning = has winning product details
    const hasWinning = !!data.winning;
    if (hasWinning) {
      metrics.winning.total++;
      if (data.winning?.status === 'Completed' && data.winningImages?.status === 'Completed') {
        metrics.winning.completed++;
      } else if (data.winning?.status === 'In Progress' || 
                 data.winningImages?.status === 'In Progress' ||
                 data.winning?.status === 'Completed') {
        metrics.winning.inProgress++;
      }
    }

    // Shopify = has shopify data
    const hasShopify = !!data.shopify;
    if (hasShopify) {
      metrics.shopify.total++;
      if (data.shopify?.status === 'Completed') {
        metrics.shopify.completed++;
      } else if (data.shopify?.status === 'In Progress' || 
                 data.shopify?.status === 'Images Uploaded') {
        metrics.shopify.inProgress++;
      }
    }
  });

  // Calculate completion rates
  Object.keys(metrics).forEach(stage => {
    const { total, completed } = metrics[stage];
    metrics[stage].completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  });

  return metrics;
}

// Get display name for a product (prefer winning product name, fallback to ecom_id)
export function getProductDisplayName(ecomId, winningDetails) {
  if (!winningDetails) return ecomId;
  const winning = Array.isArray(winningDetails) 
    ? winningDetails.find(w => w.ecom_id === ecomId)
    : winningDetails;
  return winning?.product_name || ecomId;
}

// Truncate text with ellipsis
export function truncateText(text, maxLength = 200) {
  if (!text || text.length <= maxLength) return { text: text || '', isTruncated: false };
  return { text: text.slice(0, maxLength) + '...', isTruncated: true };
}
